<script setup lang="ts">
/**
 * The enterprise's memory as a constellation (Operations → Memory, and the site's /product/memory): every memory a
 * dot in one field, coloured by when it was mentioned and sized by what stands behind it, over a faint web of the
 * links between them. Pointing at a memory dims the rest, lights its own links by kind, and opens a card saying what
 * it is. Picking one says which (`select`); `drawn` makes the field a picture that can be explored but not picked from.
 *
 * Data in (lib/surfaces.ts ConstellationUnitView and ConstellationLinkView, shaped after
 * packages/shared/src/memory-constellation.ts), drawing out. Places come from lib/constellation.ts, by each memory's
 * id alone.
 *
 * One canvas draws the field, because a memory holds hundreds of dots and thousands of links. Everything a reader
 * has to read is real text over it: the key, the controls, and the card. The canvas has no colour of its own: it
 * reads the theme's tokens once it is mounted, so a palette change reaches it like any other component.
 *
 * - Colour: the oldest memory on the page is ink, the newest electric cyan (memory's data colour).
 * - Links: similar meaning and close in time are cyan, a shared entity and an explanation are dirty white; in each
 *   pair the second is dashed. Similarity and timing are never drawn as proof.
 * - Moving: a mouse drags the field and ⌘/Ctrl + scroll zooms it; a plain scroll is the page's, and so is a touch.
 * - Motion is slow and can be paused (BG §22.2). It starts paused for a reader who asked for less motion, and the
 *   choice is remembered. Paused, off screen or in a hidden tab, nothing is drawn until something changes.
 *
 * A canvas cannot be read aloud or tabbed through. A page that mounts this lists the same memories beside it.
 */
import { Minus, Pause, Play, Plus, Scan } from '@lucide/vue'
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import {
    type ConstellationView,
    fitZoom,
    heatOf,
    type IndexedLink,
    indexLinks,
    mentionedRange,
    nearest,
    placeConstellation,
    radiusOf,
    toScreen,
    zoomAbout,
} from '../lib/constellation'
import {
    CONSTELLATION_LINK_WORD,
    type ConstellationLinkKind,
    type ConstellationLinkView,
    type ConstellationUnitView,
} from '../lib/surfaces'

const props = defineProps<{
    units: readonly ConstellationUnitView[]
    links: readonly ConstellationLinkView[]
    /** The memories being read, ringed in cyan. */
    selectedIds?: readonly string[]
    label?: string
    drawn?: boolean
    /** The words to say each kind of link in, over the kit's own. */
    linkWords?: Partial<Record<ConstellationLinkKind, string>>
}>()
const emit = defineEmits<{ select: [id: string] }>()

const KINDS: readonly ConstellationLinkKind[] = ['semantic', 'temporal', 'entity', 'causal']
/** Which ink a kind of link is drawn in, and whether it is dashed. The key below draws the same four in CSS. */
const LINK_STYLE: Readonly<Record<ConstellationLinkKind, { ink: 'cyan' | 'white'; dash: number[] }>> = {
    semantic: { ink: 'cyan', dash: [] },
    temporal: { ink: 'cyan', dash: [5, 4] },
    entity: { ink: 'white', dash: [] },
    causal: { ink: 'white', dash: [5, 4] },
}

const MOTION_KEY = 'zh-constellation-motion'
const RAMP_STEPS = 16
/** The most links the idle web draws: past this a reader sees a haze either way, and a frame takes too long. */
const MAX_IDLE_LINKS = 6000
/** How near a pointer must be to a dot to be pointing at it, in pixels. */
const POINTING = 28
/** A press that travels less than this is a click, not a drag. */
const CLICK_SLOP = 4
/** Zooming runs from half the fitted field to sixteen times it. */
const ZOOM_OUT = 0.5
const ZOOM_IN = 16
const ZOOM_STEP = 1.4
/** Past this multiple of the fitted zoom there is room to name the memories. */
const LABELS_FROM = 1.6
const LABEL_CELL = 90
const LABEL_BOX = { width: 155, height: 16 }
const LABEL_LETTERS = 45
const OFFSCREEN = 60
const EASE = 0.14

interface Inks {
    ramp: string[]
    cyan: string
    white: string
    label: string
    font: string
}

const canvas = ref<HTMLCanvasElement | null>(null)
const probe = ref<HTMLElement | null>(null)
const moving = ref(true)
const dragging = ref(false)
/** The memory being pointed at, as a position in `units`, and where its card sits. */
const pointed = ref(-1)
const card = ref({ left: 0, top: 0, flipX: false, flipY: false })

const placed = computed(() => placeConstellation(props.units))
const index = computed(() => indexLinks(props.units, props.links))
const range = computed(() => mentionedRange(props.units))
const selected = computed(() => new Set(props.selectedIds ?? []))
/** Whether any memory on the page says how many source facts stand behind it: what the size key is captioned. */
const sizedByFacts = computed(() => props.units.some(unit => (unit.sourceFacts ?? 0) > 0))

const dots = computed(() =>
    placed.value.map(({ unit }, i) => {
        const linkCount = index.value.byUnit[i]?.length ?? 0
        return {
            step: Math.round(heatOf(unit.at, range.value) * (RAMP_STEPS - 1)),
            radius: radiusOf(unit.sourceFacts, linkCount),
            linkCount,
            selected: selected.value.has(unit.id),
        }
    }),
)

const linksByKind = computed(() => {
    const groups: Record<ConstellationLinkKind, IndexedLink[]> = { semantic: [], temporal: [], entity: [], causal: [] }
    for (const link of index.value.links) groups[link.link.type].push(link)
    return groups
})

const pointedUnit = computed(() => props.units[pointed.value] ?? null)
/** Whether the memory under the pointer can be picked (`open`) or only read (`read`): what the cursor says. */
const pointing = computed(() => {
    if (!pointedUnit.value) return
    return props.drawn || pointedUnit.value.opens === false ? 'read' : 'open'
})
const pointedLinks = computed(() => dots.value[pointed.value]?.linkCount ?? 0)

function day(moment: number): string {
    return new Date(moment).toISOString().slice(0, 10)
}

function linkWord(kind: ConstellationLinkKind): string {
    return props.linkWords?.[kind] ?? CONSTELLATION_LINK_WORD[kind]
}

const summary = computed(() => {
    const [memories, links] = [props.units.length, index.value.links.length]
    return `${props.label ?? 'Memory'}: ${memories} ${memories === 1 ? 'memory' : 'memories'} and ${links} ${
        links === 1 ? 'link' : 'links'
    } between them, coloured by when each was mentioned.`
})

// ── The stage: what a frame reads and writes. Not reactive, so a frame never schedules a render. ─────────────────

const stage = {
    width: 0,
    height: 0,
    ratio: 1,
    fit: 1,
    /** Whether the reader has moved the field; until then a resize refits it. */
    moved: false,
    view: { zoom: 1, panX: 0, panY: 0 } as ConstellationView,
    aim: { zoom: 1, panX: 0, panY: 0 } as ConstellationView,
    pointerX: 0,
    pointerY: 0,
    inside: false,
    drag: null as { x: number; y: number; panX: number; panY: number; travelled: number } | null,
    seconds: 0,
    last: 0,
    frame: 0,
    onScreen: true,
}
let xs = new Float32Array(0)
let ys = new Float32Array(0)
let neighbours = new Uint8Array(0)
let context: CanvasRenderingContext2D | null = null
let inks: Inks | null = null
let resizes: ResizeObserver | null = null
let sightings: IntersectionObserver | null = null

/** The theme's colours as the canvas can paint them: each read off a hidden element that resolves the token. */
function readInks(): Inks | null {
    const [swatch, field] = [probe.value, canvas.value]
    if (!swatch || !field) return null
    const read = (value: string): string => {
        swatch.style.color = value
        return getComputedStyle(swatch).color || getComputedStyle(field).color
    }
    return {
        ramp: Array.from({ length: RAMP_STEPS }, (_, step) =>
            read(`color-mix(in oklab, var(--electric-cyan) ${(step / (RAMP_STEPS - 1)) * 100}%, var(--line))`),
        ),
        cyan: read('var(--electric-cyan)'),
        white: read('var(--dirty-white)'),
        label: read('var(--ink-2)'),
        font: `11px ${getComputedStyle(field).fontFamily}`,
    }
}

function requestDraw(): void {
    if (stage.frame === 0 && typeof requestAnimationFrame === 'function') {
        stage.frame = requestAnimationFrame(frame)
    }
}

function measure(): void {
    const field = canvas.value
    if (!field) return
    const box = field.getBoundingClientRect()
    stage.width = box.width
    stage.height = box.height
    stage.ratio = window.devicePixelRatio || 1
    field.width = Math.round(box.width * stage.ratio)
    field.height = Math.round(box.height * stage.ratio)
    stage.fit = fitZoom(placed.value, stage.width, stage.height)
    if (!stage.moved) settle()
    requestDraw()
}

/** Put the whole field in view at once, with no easing: a new page of memories, or a canvas that changed size. */
function settle(): void {
    stage.moved = false
    stage.aim = { zoom: stage.fit, panX: 0, panY: 0 }
    stage.view = { ...stage.aim }
}

function fit(): void {
    stage.moved = false
    stage.aim = { zoom: stage.fit, panX: 0, panY: 0 }
    requestDraw()
}

function zoomTo(zoom: number, x: number, y: number): void {
    const bounded = Math.min(stage.fit * ZOOM_IN, Math.max(stage.fit * ZOOM_OUT, zoom))
    stage.aim = zoomAbout(stage.aim, bounded, x, y, stage.width, stage.height)
    stage.moved = true
    requestDraw()
}

function zoomBy(factor: number): void {
    zoomTo(stage.aim.zoom * factor, stage.width / 2, stage.height / 2)
}

function point(at: number): void {
    if (at === pointed.value) return
    pointed.value = at
    neighbours.fill(0)
    for (const linkAt of index.value.byUnit[at] ?? []) {
        const link = index.value.links[linkAt]
        if (!link) continue
        neighbours[link.a] = 1
        neighbours[link.b] = 1
    }
}

/** Point at whichever dot the pointer is nearest, as the last frame placed them. */
function aimAtPointer(): void {
    point(stage.inside ? nearest(xs, ys, stage.pointerX, stage.pointerY, POINTING) : -1)
}

function locate(): void {
    const all = placed.value
    for (let i = 0; i < all.length; i += 1) {
        const unit = all[i]
        if (!unit) continue
        const at = toScreen(unit, stage.view, stage.width, stage.height, stage.seconds)
        xs[i] = at.x
        ys[i] = at.y
    }
}

function frame(now: number): void {
    stage.frame = 0
    const { view, aim } = stage
    view.zoom += (aim.zoom - view.zoom) * EASE
    view.panX += (aim.panX - view.panX) * EASE
    view.panY += (aim.panY - view.panY) * EASE
    const settling =
        Math.abs(aim.zoom - view.zoom) > aim.zoom * 0.001 ||
        Math.abs(aim.panX - view.panX) > 0.5 ||
        Math.abs(aim.panY - view.panY) > 0.5
    if (!settling) stage.view = { ...aim }

    const running = moving.value && stage.onScreen && !document.hidden
    // A long gap is a tab that was away, not motion to catch up on.
    if (running && stage.last > 0) stage.seconds += Math.min(now - stage.last, 100) / 1000
    stage.last = running ? now : 0

    locate()
    if (!stage.drag) aimAtPointer()
    if (context && inks && stage.width > 0) draw(context, inks)
    // With nothing to paint on there is no next frame to ask for.
    if (context && (running || settling)) requestDraw()
}

function offscreen(x: number, y: number): boolean {
    return x < -OFFSCREEN || x > stage.width + OFFSCREEN || y < -OFFSCREEN || y > stage.height + OFFSCREEN
}

/** A link as a gentle curve, bowed to one side so links crossing the same ground stay apart. */
function curve(ctx: CanvasRenderingContext2D, link: IndexedLink): [number, number] {
    const [ax, ay, bx, by] = [xs[link.a] ?? 0, ys[link.a] ?? 0, xs[link.b] ?? 0, ys[link.b] ?? 0]
    const [mx, my] = [(ax + bx) / 2 + (by - ay) * 0.08, (ay + by) / 2 - (bx - ax) * 0.08]
    ctx.moveTo(ax, ay)
    ctx.quadraticCurveTo(mx, my, bx, by)
    return [mx, my]
}

function drawWeb(ctx: CanvasRenderingContext2D, ink: Inks): void {
    // One slow breath across the whole web, so idle links feel alive without flickering.
    const breath = 1 + 0.18 * Math.sin(stage.seconds * 0.6)
    ctx.globalAlpha = (0.06 + Math.min(stage.view.zoom * 0.04, 0.1)) * breath
    ctx.lineWidth = 0.5
    let drawn = 0
    for (const kind of KINDS) {
        ctx.strokeStyle = ink[LINK_STYLE[kind].ink]
        ctx.beginPath()
        for (const link of linksByKind.value[kind]) {
            if (drawn >= MAX_IDLE_LINKS) break
            if (offscreen(xs[link.a] ?? 0, ys[link.a] ?? 0) && offscreen(xs[link.b] ?? 0, ys[link.b] ?? 0)) continue
            curve(ctx, link)
            drawn += 1
        }
        ctx.stroke()
    }
}

/** The pointed-at memory's own links, each in its kind's line, with a bead of light running outward along it. */
function drawPointedLinks(ctx: CanvasRenderingContext2D, ink: Inks, at: number): void {
    for (const linkAt of index.value.byUnit[at] ?? []) {
        const link = index.value.links[linkAt]
        if (!link) continue
        const style = LINK_STYLE[link.link.type]
        ctx.strokeStyle = ink[style.ink]
        ctx.globalAlpha = 0.6
        ctx.lineWidth = 1.5
        ctx.setLineDash(style.dash)
        ctx.beginPath()
        const [mx, my] = curve(ctx, link)
        ctx.stroke()
        ctx.setLineDash([])

        const along = (stage.seconds * 0.22 + (linkAt % 13) / 13) % 1
        const u = link.a === at ? along : 1 - along
        const v = 1 - u
        const [ax, ay, bx, by] = [xs[link.a] ?? 0, ys[link.a] ?? 0, xs[link.b] ?? 0, ys[link.b] ?? 0]
        ctx.globalAlpha = 0.9 * (0.4 + 0.6 * Math.sin(u * Math.PI))
        ctx.fillStyle = ink[style.ink]
        ctx.beginPath()
        ctx.arc(v * v * ax + 2 * v * u * mx + u * u * bx, v * v * ay + 2 * v * u * my + u * u * by, 2, 0, 2 * Math.PI)
        ctx.fill()
    }
}

function drawDots(ctx: CanvasRenderingContext2D, ink: Inks, at: number): void {
    const { seconds, view } = stage
    const all = placed.value
    const naming = view.zoom > stage.fit * LABELS_FROM
    const claimed = new Set<string>()
    ctx.font = ink.font
    ctx.textBaseline = 'middle'

    const one = (i: number): void => {
        const [unit, dot] = [all[i], dots.value[i]]
        const [x, y] = [xs[i] ?? 0, ys[i] ?? 0]
        if (!unit || !dot || offscreen(x, y)) return
        const isPointed = i === at
        const isNeighbour = at >= 0 && neighbours[i] === 1
        // Each dot breathes a little out of step with the rest, in size and in brightness.
        const pulse = 1 + 0.13 * Math.sin(seconds * 1.05 + unit.phase)
        const radius = Math.max(2, dot.radius * pulse * Math.min(view.zoom, 2))
        const twinkle = 0.82 + 0.18 * Math.sin(seconds * 1.4 + unit.phase * 2.1)
        const colour = ink.ramp[dot.step] ?? ink.cyan

        if (dot.linkCount > 3 && at < 0) {
            ctx.globalAlpha = (0.06 + Math.min(dot.linkCount * 0.005, 0.08)) * twinkle
            ctx.fillStyle = colour
            ctx.beginPath()
            ctx.arc(x, y, radius * 2, 0, 2 * Math.PI)
            ctx.fill()
        }

        // Pointing at one memory puts it and its neighbours forward and the rest of the field back.
        // A memory with no links is still a memory: it is dimmer than a well-linked one, never faint.
        let [alpha, glow] = [(0.6 + Math.min(dot.linkCount * 0.03, 0.4)) * twinkle, 0]
        if (isPointed) [alpha, glow] = [1, 20]
        else if (isNeighbour) [alpha, glow] = [0.95, 10]
        else if (at >= 0) alpha = 0.08
        ctx.globalAlpha = alpha
        ctx.fillStyle = colour
        ctx.shadowColor = colour
        ctx.shadowBlur = glow
        ctx.beginPath()
        ctx.arc(x, y, radius, 0, 2 * Math.PI)
        ctx.fill()
        ctx.shadowBlur = 0

        if (dot.selected) {
            ctx.globalAlpha = at >= 0 && !isPointed && !isNeighbour ? 0.3 : 1
            ctx.strokeStyle = ink.cyan
            ctx.lineWidth = 1.5
            ctx.beginPath()
            ctx.arc(x, y, radius + 3.5, 0, 2 * Math.PI)
            ctx.stroke()
        }

        // The card names the pointed-at memory; its neighbours and, zoomed in, every memory with room are named here.
        if (isPointed || !(isNeighbour || (naming && at < 0))) return
        const [left, top] = [x + radius + 5, y - LABEL_BOX.height / 2]
        const cells: string[] = []
        for (let c = Math.floor(left / LABEL_CELL); c <= Math.floor((left + LABEL_BOX.width) / LABEL_CELL); c += 1) {
            for (let r = Math.floor(top / LABEL_CELL); r <= Math.floor((top + LABEL_BOX.height) / LABEL_CELL); r += 1) {
                cells.push(`${c},${r}`)
            }
        }
        if (cells.some(cell => claimed.has(cell))) return
        for (const cell of cells) claimed.add(cell)
        const text = unit.unit.text
        ctx.globalAlpha = isNeighbour ? 0.85 : Math.min(1, (view.zoom / stage.fit - LABELS_FROM) * 2.5 + 0.25)
        ctx.fillStyle = ink.label
        ctx.fillText(text.length > LABEL_LETTERS ? `${text.slice(0, LABEL_LETTERS)}…` : text, left, y)
    }

    for (let i = 0; i < all.length; i += 1) if (i !== at) one(i)
    if (at >= 0) one(at)
}

function draw(ctx: CanvasRenderingContext2D, ink: Inks): void {
    const at = pointed.value
    ctx.setTransform(stage.ratio, 0, 0, stage.ratio, 0, 0)
    ctx.clearRect(0, 0, stage.width, stage.height)
    if (at >= 0) drawPointedLinks(ctx, ink, at)
    else drawWeb(ctx, ink)
    drawDots(ctx, ink, at)
    ctx.globalAlpha = 1
}

// ── The pointer ──────────────────────────────────────────────────────────────────────────────────────────────────

function track(event: PointerEvent | WheelEvent): void {
    const box = canvas.value?.getBoundingClientRect()
    stage.pointerX = event.clientX - (box?.left ?? 0)
    stage.pointerY = event.clientY - (box?.top ?? 0)
    stage.inside = true
    // The card sits beside the pointer, on whichever side has room.
    card.value = {
        left: stage.pointerX,
        top: stage.pointerY,
        flipX: stage.pointerX > stage.width / 2,
        flipY: stage.pointerY > stage.height / 2,
    }
}

function onPointerDown(event: PointerEvent): void {
    track(event)
    aimAtPointer()
    // A touch scrolls the page; only a mouse or a pen drags the field.
    if (event.pointerType === 'touch' || event.button !== 0) return
    stage.drag = { x: event.clientX, y: event.clientY, panX: stage.aim.panX, panY: stage.aim.panY, travelled: 0 }
    canvas.value?.setPointerCapture?.(event.pointerId)
}

function onPointerMove(event: PointerEvent): void {
    track(event)
    const drag = stage.drag
    if (!drag) {
        aimAtPointer()
        requestDraw()
        return
    }
    const [dx, dy] = [event.clientX - drag.x, event.clientY - drag.y]
    drag.travelled = Math.max(drag.travelled, Math.hypot(dx, dy))
    if (drag.travelled <= CLICK_SLOP) return
    dragging.value = true
    stage.moved = true
    stage.aim = { zoom: stage.aim.zoom, panX: drag.panX + dx, panY: drag.panY + dy }
    stage.view = { ...stage.view, panX: stage.aim.panX, panY: stage.aim.panY }
    point(-1)
    requestDraw()
}

function onPointerUp(event: PointerEvent): void {
    const drag = stage.drag
    stage.drag = null
    dragging.value = false
    canvas.value?.releasePointerCapture?.(event.pointerId)
    // A finger that scrolled the page was cancelled, never lifted here: a touch that ends here is a tap.
    const clicked = event.pointerType === 'touch' || (drag !== null && drag.travelled <= CLICK_SLOP)
    if (!clicked) return
    track(event)
    aimAtPointer()
    const unit = pointedUnit.value
    if (unit && !props.drawn && unit.opens !== false) emit('select', unit.id)
    requestDraw()
}

function onPointerLeave(event: PointerEvent): void {
    // A finger lifting is also a pointer leaving: its card stays until the next tap.
    if (event.pointerType === 'touch' && event.type !== 'pointercancel') return
    stage.inside = false
    stage.drag = null
    dragging.value = false
    point(-1)
    requestDraw()
}

function onWheel(event: WheelEvent): void {
    if (!event.ctrlKey && !event.metaKey) return
    event.preventDefault()
    track(event)
    zoomTo(stage.aim.zoom * Math.exp(-event.deltaY * 0.01), stage.pointerX, stage.pointerY)
}

// ── Motion, and the canvas's life ────────────────────────────────────────────────────────────────────────────────

function remembered(): string | null {
    try {
        return window.localStorage.getItem(MOTION_KEY)
    } catch {
        return null
    }
}

function toggleMotion(): void {
    moving.value = !moving.value
    try {
        window.localStorage.setItem(MOTION_KEY, moving.value ? 'on' : 'off')
    } catch {
        // A browser that keeps nothing still pauses; it just asks again next time.
    }
    requestDraw()
}

function rebuild(): void {
    const count = placed.value.length
    xs = new Float32Array(count)
    ys = new Float32Array(count)
    neighbours = new Uint8Array(count)
    pointed.value = -1
    stage.fit = fitZoom(placed.value, stage.width, stage.height)
    settle()
    requestDraw()
}

watch([placed, index], rebuild)
watch(dots, requestDraw)

onMounted(() => {
    const choice = remembered()
    const calm = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true
    moving.value = choice === null ? !calm : choice !== 'off'

    context = canvas.value?.getContext('2d') ?? null
    inks = readInks()
    rebuild()
    measure()
    if (canvas.value && typeof ResizeObserver === 'function') {
        resizes = new ResizeObserver(measure)
        resizes.observe(canvas.value)
    }
    if (canvas.value && typeof IntersectionObserver === 'function') {
        sightings = new IntersectionObserver(entries => {
            stage.onScreen = entries.some(entry => entry.isIntersecting)
            requestDraw()
        })
        sightings.observe(canvas.value)
    }
    document.addEventListener('visibilitychange', requestDraw)
})

onBeforeUnmount(() => {
    if (stage.frame !== 0) cancelAnimationFrame(stage.frame)
    resizes?.disconnect()
    sightings?.disconnect()
    document.removeEventListener('visibilitychange', requestDraw)
})
</script>

<template>
    <div class="zh-kc" :data-drawn="drawn ? 'true' : undefined">
        <span ref="probe" class="zh-kc__probe" aria-hidden="true" />
        <canvas
            ref="canvas"
            class="zh-kc__field"
            role="img"
            :aria-label="summary"
            :data-pointing="pointing"
            :data-dragging="dragging ? 'true' : undefined"
            @pointerdown="onPointerDown"
            @pointermove="onPointerMove"
            @pointerup="onPointerUp"
            @pointerleave="onPointerLeave"
            @pointercancel="onPointerLeave"
            @wheel="onWheel"
        />

        <div class="zh-kc__key" aria-hidden="true">
            <template v-if="range">
                <p class="zh-kc__cap">Mentioned</p>
                <span class="zh-kc__ramp" />
                <p class="zh-kc__ends">
                    <span>{{ day(range.min) }}</span>
                    <span>{{ day(range.max) }}</span>
                </p>
            </template>
            <p class="zh-kc__cap" data-key="size">{{ sizedByFacts ? 'Source facts' : 'Links' }}</p>
            <p class="zh-kc__sizes">few <i /><i /><i /> many</p>
        </div>

        <div class="zh-kc__tools">
            <button type="button" class="zh-kc__tool" aria-label="Zoom out" title="Zoom out" @click="zoomBy(1 / ZOOM_STEP)">
                <Minus aria-hidden="true" />
            </button>
            <button type="button" class="zh-kc__tool" aria-label="Zoom in" title="Zoom in" @click="zoomBy(ZOOM_STEP)">
                <Plus aria-hidden="true" />
            </button>
            <button type="button" class="zh-kc__tool" aria-label="Fit" title="Fit every memory in view" @click="fit">
                <Scan aria-hidden="true" />
            </button>
            <button
                type="button"
                class="zh-kc__tool"
                data-tool="motion"
                :aria-label="moving ? 'Pause motion' : 'Play motion'"
                :title="moving ? 'Pause the ambient motion' : 'Resume the ambient motion'"
                @click="toggleMotion"
            >
                <component :is="moving ? Pause : Play" aria-hidden="true" />
                <span class="zh-kc__tool-word">{{ moving ? 'Pause' : 'Play' }}</span>
            </button>
        </div>

        <p class="zh-kc__hint" aria-hidden="true">
            <span>Drag to move</span>
            <span>⌘ or Ctrl + scroll to zoom</span>
            <span>Point at a memory to trace it</span>
        </p>

        <ul v-if="index.links.length > 0" class="zh-kc__links" aria-label="What each line means">
            <li v-for="kind in KINDS" :key="kind"><i class="zh-kc__line" :data-kind="kind" />{{ linkWord(kind) }}</li>
        </ul>

        <div
            v-if="pointedUnit && !dragging"
            class="zh-kc__card"
            aria-hidden="true"
            :data-flip-x="card.flipX ? 'true' : undefined"
            :data-flip-y="card.flipY ? 'true' : undefined"
            :style="{ '--at-x': `${card.left}px`, '--at-y': `${card.top}px` }"
        >
            <p class="zh-kc__card-head">
                <span class="zh-kc__card-type">{{ pointedUnit.typeWord }}</span>
                <span>{{ pointedLinks }} {{ pointedLinks === 1 ? 'link' : 'links' }}</span>
            </p>
            <p class="zh-kc__card-text">{{ pointedUnit.text }}</p>
            <dl v-if="pointedUnit.atWord || pointedUnit.sourceFacts" class="zh-kc__card-facts">
                <div v-if="pointedUnit.atWord">
                    <dt>Mentioned</dt>
                    <dd>{{ pointedUnit.atWord }}</dd>
                </div>
                <div v-if="pointedUnit.sourceFacts">
                    <dt>Source facts</dt>
                    <dd>{{ pointedUnit.sourceFacts }}</dd>
                </div>
            </dl>
            <p v-if="pointedUnit.entities.length > 0" class="zh-kc__card-chips">
                <span v-for="entity in pointedUnit.entities" :key="entity">{{ entity }}</span>
            </p>
        </div>
    </div>
</template>

<style scoped>
.zh-kc {
    container: zh-kc / inline-size;
    position: relative;
    /* A stated width: left to itself, a narrow field would take its width from its least height and the ratio. */
    width: 100%;
    aspect-ratio: 16 / 10;
    min-height: 20rem;
    overflow: hidden;
    color: var(--ink-3);
    font-family: var(--font-body);
}

.zh-kc__probe {
    display: none;
}

.zh-kc__field {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    cursor: grab;
}

.zh-kc__field[data-pointing="open"] {
    cursor: pointer;
}

.zh-kc__field[data-pointing="read"] {
    cursor: default;
}

.zh-kc__field[data-dragging] {
    cursor: grabbing;
}

/* ── The key: real text over the field, out of the pointer's way ─────────────────────────── */

.zh-kc__key,
.zh-kc__links,
.zh-kc__hint,
.zh-kc__card {
    position: absolute;
    margin: 0;
    pointer-events: none;
}

/* The field runs under its own key, so each piece of the key carries enough ground to stay readable over it. */
.zh-kc__key,
.zh-kc__links,
.zh-kc__hint {
    padding: 0.375rem 0.5rem;
    background: color-mix(in srgb, var(--panel) 85%, transparent);
}

.zh-kc__key {
    top: 0.375rem;
    left: 0.25rem;
    display: grid;
    gap: 0.25rem;
    justify-items: start;
}

.zh-kc__key p {
    margin: 0;
}

.zh-kc__cap,
.zh-kc__links,
.zh-kc__card-head,
.zh-kc__card-facts dt {
    font-family: var(--font-mono);
    font-size: 0.625rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
}

.zh-kc__cap {
    color: var(--ink-2);
}

.zh-kc__cap[data-key="size"] {
    margin-top: 0.5rem;
}

/* The same two inks the canvas mixes its dots from: the oldest memory to the newest. */
.zh-kc__ramp {
    width: 100%;
    min-width: 9rem;
    height: 0.375rem;
    background: linear-gradient(to right in oklab, var(--line), var(--electric-cyan));
}

.zh-kc__ends {
    display: flex;
    justify-content: space-between;
    gap: 0.75rem;
    width: 100%;
}

.zh-kc__ends,
.zh-kc__sizes,
.zh-kc__hint {
    font-family: var(--font-mono);
    font-size: 0.6875rem;
}

.zh-kc__sizes {
    display: flex;
    align-items: center;
    gap: 0.375rem;
}

.zh-kc__sizes i {
    width: 0.25rem;
    height: 0.25rem;
    border-radius: 50%;
    background: var(--ink-3);
}

.zh-kc__sizes i:nth-of-type(2) {
    width: 0.5rem;
    height: 0.5rem;
}

.zh-kc__sizes i:nth-of-type(3) {
    width: 0.75rem;
    height: 0.75rem;
}

.zh-kc__hint {
    bottom: 0.375rem;
    left: 0.25rem;
    display: none;
    gap: 1rem;
}

.zh-kc__links {
    right: 0.25rem;
    bottom: 0.375rem;
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 0.25rem 0.875rem;
    padding: 0;
    list-style: none;
}

.zh-kc__links li {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
}

.zh-kc__line {
    width: 1.5rem;
    border-top: 2px solid var(--electric-cyan);
}

.zh-kc__line[data-kind="temporal"],
.zh-kc__line[data-kind="causal"] {
    border-top-style: dashed;
}

.zh-kc__line[data-kind="entity"],
.zh-kc__line[data-kind="causal"] {
    border-top-color: var(--dirty-white);
}

/* ── The controls ────────────────────────────────────────────────────────────────────────── */

.zh-kc__tools {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    display: flex;
    gap: 0.375rem;
}

.zh-kc__tool {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    min-width: 1.75rem;
    height: 1.75rem;
    padding: 0 0.4375rem;
    border: 1px solid var(--line);
    background: var(--void-black);
    color: var(--ink-2);
    font-family: var(--font-mono);
    font-size: 0.6875rem;
    font-weight: 500;
    letter-spacing: var(--tracking-system);
    text-transform: uppercase;
    cursor: pointer;
}

/* The word beside the icon is for a field with room; a narrow one keeps the icon and its name. */
.zh-kc__tool-word {
    display: none;
}

.zh-kc__tool :deep(svg) {
    width: 0.875rem;
    height: 0.875rem;
}

.zh-kc__tool:hover {
    border-color: var(--line-strong);
    color: var(--ink-1);
}

.zh-kc__tool:focus-visible {
    outline: 2px solid var(--electric-cyan);
    outline-offset: 2px;
}

/* ── The card: the memory being pointed at ───────────────────────────────────────────────── */

/* Beside the pointer (--at-x, --at-y), on whichever side has room, and never past the field's own edges. */
.zh-kc__card {
    --card-width: min(20rem, 70cqw);
    --card-gap: 0.875rem;
    --card-edge: 0.25rem;

    z-index: 1;
    top: calc(var(--at-y) + var(--card-gap));
    left: clamp(
        var(--card-edge),
        calc(var(--at-x) + var(--card-gap)),
        calc(100% - var(--card-width) - var(--card-edge))
    );
    display: grid;
    gap: 0.5rem;
    box-sizing: border-box;
    width: var(--card-width);
    padding: 0.75rem 0.875rem;
    border: 1px solid var(--line);
    background: var(--panel);
    color: var(--ink-1);
}

.zh-kc__card[data-flip-x] {
    left: clamp(
        var(--card-edge),
        calc(var(--at-x) - var(--card-width) - var(--card-gap)),
        calc(100% - var(--card-width) - var(--card-edge))
    );
}

.zh-kc__card[data-flip-y] {
    top: auto;
    bottom: calc(100% - var(--at-y) + var(--card-gap));
}

.zh-kc__card p,
.zh-kc__card dl,
.zh-kc__card dd {
    margin: 0;
}

.zh-kc__card-head {
    display: flex;
    align-items: baseline;
    gap: 0.625rem;
    color: var(--ink-3);
}

.zh-kc__card-type {
    color: var(--electric-cyan);
}

.zh-kc__card-text {
    display: -webkit-box;
    overflow: hidden;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.4;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 5;
}

.zh-kc__card-facts {
    display: grid;
    gap: 0.25rem;
    padding-top: 0.5rem;
    border-top: 1px solid var(--hair);
}

.zh-kc__card-facts div {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 0.75rem;
}

.zh-kc__card-facts dt {
    color: var(--ink-3);
}

.zh-kc__card-facts dd {
    font-family: var(--font-mono);
    font-size: 0.75rem;
}

.zh-kc__card-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
}

.zh-kc__card-chips span {
    padding: 0.0625rem 0.375rem;
    border: 1px solid var(--hair);
    color: var(--ink-2);
    font-size: 0.75rem;
}

@container zh-kc (min-width: 60rem) {
    .zh-kc__hint {
        display: flex;
    }

    /* The hint has the bottom-left corner, so the key to the lines keeps to its own side. */
    .zh-kc__links {
        max-width: 50%;
    }

    .zh-kc__tool-word {
        display: inline;
    }
}
</style>
